/* eslint-disable max-lines -- the scroll-target state machine (five
 * interlocking effects: channel-switch reset, request-handler,
 * resolution, layout-scroll, completion) is shared state between the
 * message window, the load-more sentinel, and the notification
 * deep-link path. Refactoring it into a hook would require threading
 * 15+ refs/setters and risk subtle behavior changes to a central UX
 * surface. Splitting further is tracked in
 * plans/cached-singing-fiddle.md. Smaller seams (viewport height,
 * warm preload, clipboard copy, render diagnostics, windowed-item
 * projection, auto-scroll-on-new-message, highlight target,
 * load-more pill, jump-to-bottom button, row builder) have already
 * been extracted into message-list-view/. */
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MessageDataStore } from "@/lib/message-data-store";
import { csMark, csNow, csLog } from "@/lib/channel-switch-profile";
import {
  useReactMessageView,
  type MessageEntrySource,
} from "@/lib/react-message-view";
import {
  useChannelReactionsMap,
  useChannelCommunitySignalsMap,
  useChannelAttachmentsMap,
  useChannelLinkPreviewsMap,
} from "@/lib/selectors/composite-channel";
import { useSingleOpenMessageAction } from "@/components/messages/use-single-open-message-action";
import { useScrollAnchor } from "@/hooks/use-scroll-anchor";
import { useMessageWindow } from "@/hooks/use-message-window";
import { useLoadMore } from "@/hooks/use-load-more";
import {
  MessageScrollContainer,
  type ChannelMessagesStatus,
} from "./MessageScrollContainer";
import { toast } from "sonner";
import { SelectionToolbar } from "@/components/messages/SelectionToolbar";
import { RENDER_DEBUG, SINGLETON_MESSAGE_ACTIONS } from "@/lib/debug-flags";
import { HoveredMessageActions } from "@/components/messages/HoveredMessageActions";
import { HoveredMessageProvider } from "@/components/messages/use-hovered-message";

import { useViewportHeight } from "./message-list-view/use-viewport-height";
import { useWarmPreload } from "./message-list-view/use-warm-preload";
import { useClipboardCopy } from "./message-list-view/use-clipboard-copy";
import { useRenderDiagnostics } from "./message-list-view/use-render-diagnostics";
import { useWindowedItems } from "./message-list-view/use-windowed-items";
import { useHighlightTarget } from "./message-list-view/use-highlight-target";
import { useAutoScrollNewMessages } from "./message-list-view/use-auto-scroll-new-messages";
import { LoadingOlderPill } from "./message-list-view/loading-older-pill";
import { JumpToBottomButton } from "./message-list-view/jump-to-bottom-button";
import { renderMessageRows } from "./message-list-view/render-message-rows";
import type {
  MessageListViewHandle,
  MessageListViewProps,
  ScrollRequest,
} from "./message-list-view/message-list-types";

// ─── Constants ──────────────────────────────────────────────────────

const TARGET_SCROLL_DOM_RETRY_FRAMES = 6;

/**
 * Average row height used to convert `viewportHeight` → `viewportRows`
 * for window sizing. Must match `DEFAULT_AVG_ROW_HEIGHT` inside
 * `useMessageWindow`; both express the same heuristic ("a typical chat
 * row is ~80px tall") and there's no benefit to letting them diverge.
 */
const SIZING_AVG_ROW_HEIGHT = 80;

/**
 * Multiplier on visible rows for the channel-restore slice. The slice
 * must extend far enough below the anchor that the user's first wheel
 * tick has scrollable headroom — otherwise scrollTop pins at
 * maxScrollTop, no scroll events fire, and shift-down never triggers
 * ("stuck can't scroll down"). At `OLDER_BIAS = 0.6`, a 3× viewport
 * slice puts ~1.2× viewport of rows below the anchor, leaving comfortable
 * headroom in both directions regardless of window size.
 *
 * The floor (30 rows) preserves prior behavior on small viewports
 * (laptop ~600-800px message area). On larger windows the slice scales
 * with the viewport so a 1500px-tall message area gets ~57 rows instead
 * of the previous fixed 30 — first-paint cost grows accordingly, but
 * staying at 30 reproduces the stuck condition once the viewport itself
 * exceeds the slice's below-anchor rows.
 */
const CHANNEL_RESTORE_VIEWPORT_MULTIPLIER = 3;
const CHANNEL_RESTORE_MIN_ROWS = 30;

/**
 * First-paint slice for the resetKey-changed branch in `useMessageWindow`.
 * Intentionally smaller than the channel-restore dispatch size (15-row
 * floor vs 30) so the very first paint after a channel switch stays
 * tight on small viewports — the follow-up focus-target dispatch grows
 * it to the full channel-restore size. On tall viewports the same
 * viewport-scaling logic kicks in to avoid the stuck condition on the
 * first frame.
 */
const ANCHOR_FIRST_PAINT_MIN_ROWS = 15;

// ─── Types (re-exported for back-compat with consumers) ─────────────

export type { MessageListViewHandle, MessageListViewProps, ScrollRequest };

/**
 * Scroll target state machine: resolving → found → scrolled → cleared.
 * Used for notification deep-links, channel-restore, and in-app jumps
 * (pinned messages, thread "jump to parent"). Stays internal to this
 * file because the surrounding effects own its lifecycle.
 */
interface ScrollTarget {
  messageId: string;
  fallbackMessageId?: string;
  source: "notification" | "channel-restore" | "in-app-jump";
  behavior: "center" | "anchor-offset";
  savedOffset?: number;
  status: "resolving" | "found" | "scrolled";
  /** Monotonic ID for deduplication of external scroll requests */
  requestId?: number;
}

// ─── Component ──────────────────────────────────────────────────────

export const MessageListView = memo(
  forwardRef<MessageListViewHandle, MessageListViewProps>(
    function MessageListView(
      {
        channelId,
        messageList,
        hasMoreMessages,
        channelMessagesStatus,
        isUsingReconnectSnapshot,
        showMessageLoadingSkeleton,
        messageListLength,
        renderUserMap,
        pinnedMessageIds,
        workspaceMembers,
        workspaceChannels,
        activeWorkspaceMemberIds,
        messageById,
        threadSourceIds,
        scrollAnchorByChannel,
        currentAnchorIdRef,
        bumpInFlightRef,
        onBumpLimit,
        onThreadClick,
        onInlineReply,
        onCreateThread,
        onScrollToMessage,
        onUserClick,
        onChannelClick,
        onPin,
        onSendToShell,
        scrollRequest,
        onScrollRequestHandled,
        activeCallMessageId,
        onJoinCall,
        onReturnToBottom,
        messageLimit,
        messagePageSize,
        userId,
        hidden = false,
      },
      ref,
    ) {
      // Channel-switch profile — body + commit timing for the
      // (probably) heaviest descendant of MessageArea. The 22 hooks +
      // memoized children (MessageRow tree, virtualization, scroll
      // anchor, viewport height, etc.) all settle inside this fiber.
      const csRenderStart = csNow();
      const csRenderStartRef = useRef(0);

      csRenderStartRef.current = csRenderStart;

      useLayoutEffect(() => {
        csLog(
          "MessageListView.commit",
          csRenderStartRef.current,
          `ch=${channelId?.slice(-6) ?? "-"} msgs=${messageListLength}`,
        );
      });

      // ─── Render diagnostics ─────────────────────────────────────────
      // Prop bag MUST mirror the function's parameter list above.
      // `React.memo` shallow-compares all props; if a prop is missing
      // from this diagnostic, a memo bust on that prop will mis-report
      // as "INTERNAL (no prop change)".
      useRenderDiagnostics(
        "MessageListView",
        `ch=${channelId?.slice(-6)} msgs=${messageListLength}`,
        {
          channelId,
          messageList,
          hasMoreMessages,
          channelMessagesStatus,
          isUsingReconnectSnapshot,
          showMessageLoadingSkeleton,
          messageListLength,
          renderUserMap,
          pinnedMessageIds,
          workspaceMembers,
          workspaceChannels,
          activeWorkspaceMemberIds,
          messageById,
          threadSourceIds,
          scrollAnchorByChannel,
          currentAnchorIdRef,
          bumpInFlightRef,
          onBumpLimit,
          onThreadClick,
          onInlineReply,
          onCreateThread,
          onScrollToMessage,
          onUserClick,
          onChannelClick,
          onPin,
          onSendToShell,
          scrollRequest,
          onScrollRequestHandled,
          activeCallMessageId,
          onJoinCall,
          onReturnToBottom,
          messageLimit,
          messagePageSize,
          userId,
          hidden,
        },
      );

      // ─── Scroll infrastructure ──────────────────────────────────────
      const scrollRef = useRef<HTMLDivElement>(null);
      const isNearBottomRef = useRef(true);
      const [showJumpToBottom, setShowJumpToBottom] = useState(false);
      const [newMessagesPill, setNewMessagesPill] = useState(0);

      const scrollAnchor = useScrollAnchor(scrollRef);

      // ─── Action-pill state ──────────────────────────────────────────
      // NOTE: expose `activeMessageId` (primitive string | null) rather than
      // an `isMessageOpen` callback. A callback would get a new identity
      // every time activeMessageId changes, breaking MessageRow's memo for
      // every row in the list. With a primitive, only the two rows whose
      // `actionPillOpen` boolean flips actually re-render.
      const {
        activeMessageId,
        setMessageOpen,
        closeAll: closeAllActionPills,
      } = useSingleOpenMessageAction();

      // Reset action pills on channel switch
      useEffect(() => {
        closeAllActionPills();
      }, [channelId, closeAllActionPills]);

      // ─── Viewport measurement ───────────────────────────────────────
      const scrollAreaRef = useRef<HTMLDivElement>(null);
      const viewportHeight = useViewportHeight(scrollAreaRef);

      // ─── messageIndexById ───────────────────────────────────────────
      const messageIndexById = useMemo(() => {
        const map = new Map<string, number>();
        for (let i = 0; i < messageList.length; i++) {
          map.set(messageList[i].id, i);
        }
        return map;
      }, [messageList]);

      // ─── Entry source: per-message data subscriptions ──────────────
      //
      // Two paths, same interface (MessageEntrySource):
      //
      // NORMAL: ReactMessageView (push-based). Zero's IVM pushes individual
      //   add/edit/remove events → view updates one Map entry → notifies one
      //   subscriber. Each MessageRow re-renders only when ITS message changes.
      //   Raw ZQL (no whereExists) → IVM tracks only the messages table.
      //   Security: data is already permission-filtered by MessageArea's
      //   useQuery which uses defineQuery with whereExists.
      //
      // RECONNECT: MessageDataStore (diff-based fallback). During reconnect
      //   snapshot mode, the push-based view might not have the snapshot's
      //   messages. The store diffs messageList and notifies per-ID subscribers.
      //   Lazy — only populated when isUsingReconnectSnapshot is true.
      //
      const messageView = useReactMessageView(channelId, messageLimit);
      const storeRef = useRef(new MessageDataStore());
      // Synchronous hydration during render — Map populated BEFORE MessageRow reads
      if (isUsingReconnectSnapshot) {
        storeRef.current.update(messageList);
      }
      // Deferred notifications — subscriber re-renders after render completes
      useEffect(() => {
        if (isUsingReconnectSnapshot) {
          storeRef.current.flush();
        }
      }, [isUsingReconnectSnapshot, messageList]);
      const entrySource: MessageEntrySource = isUsingReconnectSnapshot
        ? storeRef.current
        : messageView;

      // ─── Child-table Maps — render-isolated subscriptions ───────────
      // Three independent `useZeroSelector` subscriptions on top of the
      // SAME shared `byChannelComposite` materialization (keyed by the
      // ViewCache via `messages.byChannelComposite:{channelId}:{limit}`
      // — same key MessageArea uses, so all four consumers refcount-share
      // one server subscription).
      //
      // Each hook only fires React when ITS slice of the composite
      // changes — a reactions update doesn't re-render attachments
      // consumers, and message edits don't re-render anyone here.
      // Per-message group identity is preserved via
      // `createIncrementalGroupSelector`, so MessageRow's `memo` skips
      // re-rendering rows whose child arrays haven't changed.
      const [reactionsMap] = useChannelReactionsMap(channelId, messageLimit);
      const [signalsMap] = useChannelCommunitySignalsMap(
        channelId,
        messageLimit,
      );
      const [attachmentsMap] = useChannelAttachmentsMap(
        channelId,
        messageLimit,
      );
      const [linkPreviewsMap] = useChannelLinkPreviewsMap(
        channelId,
        messageLimit,
      );

      // Ref kept in sync with `scrollTargetPending` further down — declared
      // here so we can pass it into `useMessageWindow`. The hook reads
      // `.current` during Phase 1 to skip prepend-reconciliation while
      // a scroll-target navigation is in progress, breaking the
      // feedback loop where scrollTop animation → captureSnapshot →
      // bounds recenter → DOM shift → captureSnapshot → … See
      // `suppressReconciliationRef` in use-message-window.ts.
      const suppressReconciliationRef = useRef(false);

      // ─── Message window hook ────────────────────────────────────────
      //
      // Read the saved anchor for the new channel during render so the
      // hook can seed the initial slice around it on the resetKey
      // transition — i.e. when the user switches back to a
      // scrolled-up channel, the FIRST paint already has the right
      // rows in the DOM (paired with the cdu-side scrollTop apply in
      // MessageScrollContainer). When there's no saved anchor (cold /
      // near-bottom switch), useMessageWindow falls back to its
      // pin-to-bottom INITIAL_WINDOW_SIZE path.
      //
      // The first-paint slice is intentionally tight (matches the
      // pin-bottom paint cost) so arrow-driven switches into a
      // saved-anchor channel land as fast as cold/click switches.
      // The follow-up scrollTarget useEffect immediately dispatches a
      // focus-target with the wider channel-restore size — the
      // scrollTop adjustment in MessageScrollContainer's wbk-restore
      // layout effect keeps the anchor visually pinned across that
      // expansion, so the user doesn't perceive the slice growing.
      //
      // Both sizes scale with the viewport (3× rows visible) to keep
      // post-restore scroll headroom non-zero on tall windows. A fixed
      // row count reproduces "stuck can't scroll down" once the viewport
      // exceeds the slice's below-anchor portion. See
      // CHANNEL_RESTORE_VIEWPORT_MULTIPLIER docs above.
      const viewportRowsForSizing = Math.max(
        1,
        Math.ceil(viewportHeight / SIZING_AVG_ROW_HEIGHT),
      );
      const channelRestoreWindowSize = Math.max(
        CHANNEL_RESTORE_MIN_ROWS,
        viewportRowsForSizing * CHANNEL_RESTORE_VIEWPORT_MULTIPLIER,
      );
      const anchorFirstPaintSize = Math.max(
        ANCHOR_FIRST_PAINT_MIN_ROWS,
        viewportRowsForSizing * CHANNEL_RESTORE_VIEWPORT_MULTIPLIER,
      );
      const savedAnchorForChannel = channelId
        ? (scrollAnchorByChannel.current[channelId] ?? null)
        : null;
      const initialAnchor = savedAnchorForChannel
        ? {
            messageId: savedAnchorForChannel.id,
            windowSize: anchorFirstPaintSize,
          }
        : null;

      const messageWindow = useMessageWindow({
        messages: messageList,
        viewportHeight,
        anchorSnapshotRef: scrollAnchor.lastSnapshotRef,
        isNearBottomRef,
        // Reset bounds on channel switch — at CAP=1 single-mount the hook's
        // state persists across channels, and stale bounds past the end of
        // a smaller new channel's messages yields a blank list.
        resetKey: channelId,
        suppressReconciliationRef,
        initialAnchor,
      });

      const {
        windowMessages,
        windowBoundsKey,
        hasOlderInWindow,
        hasNewerInWindow,
        predecessorMessage,
        dispatch: dispatchWindow,
        shiftWindowUp,
        shiftWindowDown,
      } = messageWindow;

      // ─── Enriched-ID items — grouping metadata only ────────────────
      // Decouples the render loop from message content. An edit to
      // message content doesn't trigger renderedRows to rebuild — only
      // new/removed messages or user_id changes do.
      const { windowedItems, predecessorItem } = useWindowedItems(
        windowMessages,
        predecessorMessage ?? null,
      );

      // ─── Load-more hooks ────────────────────────────────────────────
      const stableOnShiftWindowUp = useCallback(() => {
        scrollAnchor.capture();
        shiftWindowUp();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- scrollAnchor is ref-based
      }, [shiftWindowUp]);

      const stableOnShiftWindowDown = useCallback(() => {
        scrollAnchor.capture();
        shiftWindowDown();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- scrollAnchor is ref-based
      }, [shiftWindowDown]);

      const stableOnBumpLimit = useCallback(() => onBumpLimit(), [onBumpLimit]);

      const { topSentinelRef, bottomSentinelRef, isLoadingOlder } = useLoadMore(
        {
          scrollRef,
          hasOlderTrimmed: hasOlderInWindow,
          hasMoreOnServer: hasMoreMessages,
          hasNewerTrimmed: hasNewerInWindow,
          onShiftWindowUp: stableOnShiftWindowUp,
          onShiftWindowDown: stableOnShiftWindowDown,
          onBumpLimit: stableOnBumpLimit,
          bumpInFlightRef,
          resetKey: channelId,
        },
      );

      // ─── Near-bottom change handler ─────────────────────────────────
      const onNearBottomChange = useCallback(
        (nearBottom: boolean) => {
          if (RENDER_DEBUG)
            console.debug(`[MessageListView] nearBottom → ${nearBottom}`);
          if (nearBottom) {
            setShowJumpToBottom(false);
            setNewMessagesPill(0);
            onReturnToBottom();
          } else {
            setShowJumpToBottom(true);
          }
        },
        [onReturnToBottom],
      );

      // ─── Auto-scroll / new messages pill ────────────────────────────
      const latestMessageId =
        messageList.length > 0 ? messageList[messageList.length - 1].id : null;

      useAutoScrollNewMessages({
        channelId,
        latestMessageId,
        messageById,
        userId,
        scrollRef,
        isNearBottomRef,
        setShowJumpToBottom,
        setNewMessagesPill,
        scrollAnchor,
        dispatchWindow,
      });

      // ─── Warm preload ───────────────────────────────────────────────
      useWarmPreload({
        channelId,
        hasMoreMessages,
        hidden,
        messageLimit,
        messagePageSize,
        isNearBottomRef,
        onBumpLimit,
      });

      // ─── Scroll target resolution ───────────────────────────────────
      const [scrollTarget, setScrollTarget] = useState<ScrollTarget | null>(
        null,
      );

      // True while we're navigating intentionally to a target message
      // (channel-switch-back to a saved anchor, notification jump,
      // in-app jump). MessageScrollContainer reads this to skip the
      // windowBoundsKey-change snapshot restore — see prop docs.
      // Also mirrored into `suppressReconciliationRef` so
      // useMessageWindow's Phase 1 stands down during navigation.
      const scrollTargetPending =
        scrollTarget !== null && scrollTarget.status !== "scrolled";
      suppressReconciliationRef.current = scrollTargetPending;

      const {
        highlightedMessageId,
        setHighlightedMessageId,
        scheduleHighlightClear,
        clearTimer: clearHighlightTimer,
      } = useHighlightTarget();

      // Reset scroll state on channel switch
      useEffect(() => {
        // Clear any stale scroll target from the previous channel
        setScrollTarget(null);
        setHighlightedMessageId(null);
        clearHighlightTimer();

        // Check if we have a saved scroll position for this channel
        const savedAnchor = channelId
          ? scrollAnchorByChannel.current[channelId]
          : undefined;

        if (savedAnchor) {
          // Restore saved position — don't pin to bottom
          isNearBottomRef.current = false;
          currentAnchorIdRef.current = savedAnchor.id;
          setScrollTarget({
            messageId: savedAnchor.id,
            fallbackMessageId: savedAnchor.neighborId ?? undefined,
            source: "channel-restore",
            behavior: "anchor-offset",
            savedOffset: savedAnchor.offset,
            status: "resolving",
          });
        } else {
          // No saved position — pin to bottom (default for new channels)
          isNearBottomRef.current = true;
          currentAnchorIdRef.current = null;
        }

        // Prune old entries to prevent unbounded growth across long sessions
        const MAX_SAVED_ANCHORS = 30;
        const anchorKeys = Object.keys(scrollAnchorByChannel.current);
        if (anchorKeys.length > MAX_SAVED_ANCHORS) {
          for (const key of anchorKeys.slice(
            0,
            anchorKeys.length - MAX_SAVED_ANCHORS,
          )) {
            delete scrollAnchorByChannel.current[key];
          }
        }

        // Clear the scroll anchor snapshot so the window hook doesn't recenter
        // around a stale anchor from the previous channel's messages
        scrollAnchor.clear();
        // Show jump-to-bottom on switch-back to a scrolled-up channel.
        // Without this, the scroll-restore lands the user mid-history but
        // the button stays hidden because the scroll handler that drives
        // it is suppressed during the channel-switch transition window.
        setShowJumpToBottom(Boolean(savedAnchor));
        setNewMessagesPill(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- scrollAnchor is ref-based, stable across renders
      }, [channelId]);

      // Enter scroll-target from external scroll request (notification / in-app-jump).
      useEffect(() => {
        if (!scrollRequest) return;
        if (RENDER_DEBUG)
          console.debug(
            `[MessageListView] scroll request received`,
            `id=${scrollRequest.requestId}`,
            `source=${scrollRequest.source}`,
            `msg=${scrollRequest.messageId.slice(-6)}`,
          );

        setScrollTarget((prev) => {
          // Don't re-enter if we already have an active target for this request
          if (prev && prev.requestId === scrollRequest.requestId) return prev;
          return {
            messageId: scrollRequest.messageId,
            source: scrollRequest.source,
            behavior: "center",
            status: "resolving",
            requestId: scrollRequest.requestId,
          };
        });
      }, [scrollRequest]);

      // Resolution effect: find target in messageIndexById, dispatch focus-target
      useEffect(() => {
        if (!scrollTarget || scrollTarget.status !== "resolving") return;

        // `channel-restore` paints just enough rows around the anchor
        // to land the saved scroll position. The default (full
        // SCROLLED_UP_MULTIPLIER ~150-200 rows) reconciles 5-6× more
        // React subtrees than the user can see in the first frame, and
        // that cost is the long pole of switch-back latency. Notification
        // / in-app-jump keep the wider default — those entries are
        // cold-into-a-channel and the user benefits from the extra
        // context above/below the target landing.
        const windowSize =
          scrollTarget.source === "channel-restore"
            ? channelRestoreWindowSize
            : undefined;

        // Try primary ID
        const primaryIdx = messageIndexById.get(scrollTarget.messageId);
        if (primaryIdx !== undefined) {
          dispatchWindow({
            type: "focus-target",
            targetIndex: primaryIdx,
            windowSize,
          });
          setScrollTarget((prev) =>
            prev ? { ...prev, status: "found" } : null,
          );
          return;
        }

        // Try fallback ID
        if (scrollTarget.fallbackMessageId) {
          const fallbackIdx = messageIndexById.get(
            scrollTarget.fallbackMessageId,
          );
          if (fallbackIdx !== undefined) {
            dispatchWindow({
              type: "focus-target",
              targetIndex: fallbackIdx,
              windowSize,
            });
            setScrollTarget((prev) =>
              prev ? { ...prev, status: "found" } : null,
            );
            return;
          }
        }

        // Not found — can we load more?
        const canBump =
          channelMessagesStatus.type === "complete" &&
          !isUsingReconnectSnapshot &&
          hasMoreMessages;

        if (canBump && !bumpInFlightRef.current) {
          onBumpLimit({ bypassCap: true });
          return; // Stay in "resolving" — effect re-fires when messageIndexById updates
        }

        // Truly not found — toast failure
        const definitelyNotFound =
          channelMessagesStatus.type === "complete" &&
          !isUsingReconnectSnapshot &&
          !hasMoreMessages;

        if (definitelyNotFound) {
          toast.error("Could not find that message");
          if (
            scrollTarget.requestId !== undefined &&
            (scrollTarget.source === "notification" ||
              scrollTarget.source === "in-app-jump")
          ) {
            onScrollRequestHandled(scrollTarget.requestId, scrollTarget.source);
          }
          setScrollTarget(null);
        }
        // If still loading (unknown status or reconnect snapshot), stay in "resolving"
      }, [
        scrollTarget,
        messageIndexById,
        dispatchWindow,
        channelMessagesStatus.type,
        isUsingReconnectSnapshot,
        hasMoreMessages,
        onBumpLimit,
        onScrollRequestHandled,
        bumpInFlightRef,
        channelRestoreWindowSize,
      ]);

      // Layout effect: when "found", find DOM element and scroll
      useLayoutEffect(() => {
        if (!scrollTarget || scrollTarget.status !== "found") return;

        const el = scrollRef.current;
        if (!el) return;

        const targetId = scrollTarget.messageId;
        let attempts = 0;

        const tryScroll = () => {
          const msgEl = el.querySelector<HTMLElement>(
            `[data-message-id="${CSS.escape(targetId)}"]`,
          );
          if (msgEl) {
            if (scrollTarget.behavior === "center") {
              msgEl.scrollIntoView({ block: "center", behavior: "auto" });
            } else if (
              scrollTarget.behavior === "anchor-offset" &&
              scrollTarget.savedOffset !== undefined
            ) {
              const containerTop = el.getBoundingClientRect().top;
              const currentOffset =
                msgEl.getBoundingClientRect().top - containerTop;
              const delta = currentOffset - scrollTarget.savedOffset;
              const before = el.scrollTop;
              el.scrollTop += delta;
              if (
                typeof localStorage !== "undefined" &&
                localStorage.getItem("ano-trace-jump") === "1"
              ) {
                console.debug(
                  `[jump] scrollTarget apply anchor-offset targetId=${targetId.slice(-6)} savedOffset=${scrollTarget.savedOffset} currentOffset=${currentOffset} delta=${delta} scrollTopBefore=${before} scrollTopAfter=${el.scrollTop} scrollHeight=${el.scrollHeight}`,
                );
              }
            }

            if (scrollTarget.source !== "channel-restore") {
              setHighlightedMessageId(targetId);
              scheduleHighlightClear();
            }
            setScrollTarget((prev) =>
              prev ? { ...prev, status: "scrolled" } : null,
            );
            return;
          }

          attempts += 1;
          if (attempts < TARGET_SCROLL_DOM_RETRY_FRAMES) {
            requestAnimationFrame(tryScroll);
          } else {
            // DOM element not found after retries — mark scrolled anyway
            setScrollTarget((prev) =>
              prev ? { ...prev, status: "scrolled" } : null,
            );
          }
        };

        // Try synchronously first (element may already be in DOM after commit).
        // Fall back to rAF retries if not yet rendered.
        tryScroll();
      }, [scrollTarget, scheduleHighlightClear, setHighlightedMessageId]);

      // Clean exit: status "scrolled" → clear on next tick
      useEffect(() => {
        if (!scrollTarget || scrollTarget.status !== "scrolled") return;
        if (RENDER_DEBUG)
          console.debug(
            `[MessageListView] scroll target completed`,
            `source=${scrollTarget.source}`,
            `msg=${scrollTarget.messageId.slice(-6)}`,
          );
        // Warm/anchor-restore path of the channel-switch profile.
        // For `channel-restore` sources this is the "settled" moment;
        // for `notification` / `in-app-jump` sources it isn't a
        // channel-switch but csMark idempotency makes the extra call
        // harmless (entry already cleared at first settled call).
        if (scrollTarget.source === "channel-restore") {
          csMark("settled", channelId);
        }

        const timer = setTimeout(() => {
          if (
            scrollTarget.requestId !== undefined &&
            (scrollTarget.source === "notification" ||
              scrollTarget.source === "in-app-jump")
          ) {
            onScrollRequestHandled(scrollTarget.requestId, scrollTarget.source);
          }
          setScrollTarget(null);
        }, 0);

        return () => clearTimeout(timer);
      }, [scrollTarget, onScrollRequestHandled]);

      // ─── Copy handler ───────────────────────────────────────────────
      useClipboardCopy({ scrollRef, channelId, messageById, renderUserMap });

      // ─── channelScrollMessagesStatus memo ───────────────────────────
      // Depend on the primitive `.type` + `.retry` rather than the whole
      // `channelMessagesStatus` object. `useQuery` re-creates the status
      // object on every IVM emission even when `.type` is unchanged; with
      // the object in deps the wrapper would re-allocate per-emit. The
      // cascade was previously caught by `MessageScrollContainer.shouldComponentUpdate`
      // (line 123 — `.type !== .type`), but the firewall is fragile (a
      // future class→function refactor would re-introduce it). See
      // `.claude/rules/queries.md` "Query-shape changes need a downstream-render audit".
      const channelScrollMessagesStatus: ChannelMessagesStatus = useMemo(() => {
        if (channelMessagesStatus.type === "error") {
          return {
            type: "error",
            retry:
              typeof channelMessagesStatus.retry === "function"
                ? channelMessagesStatus.retry
                : undefined,
          };
        }
        return { type: channelMessagesStatus.type };
      }, [channelMessagesStatus.type, channelMessagesStatus.retry]);

      // ─── Jump to bottom ─────────────────────────────────────────────
      const jumpToBottom = useCallback(() => {
        isNearBottomRef.current = true;
        scrollAnchor.capture();
        dispatchWindow({ type: "pin-bottom" });
        setShowJumpToBottom(false);
        setNewMessagesPill(0);
        if (channelId) {
          delete scrollAnchorByChannel.current[channelId];
        }
        // After React commits the pin-bottom render, scroll to actual bottom
        requestAnimationFrame(() => {
          const el = scrollRef.current;
          if (el) {
            el.scrollTop = el.scrollHeight - el.clientHeight;
          }
        });
      }, [channelId, scrollAnchor, dispatchWindow, scrollAnchorByChannel]);

      // ─── Imperative handle ──────────────────────────────────────────
      useImperativeHandle(
        ref,
        () => ({
          scrollToBottom: () => {
            isNearBottomRef.current = true;
            // Direct DOM scroll — no dispatch, no React re-render.
            // The ResizeObserver will re-pin when the new message arrives.
            const el = scrollRef.current;
            if (el) {
              el.scrollTop = el.scrollHeight;
            }
            setShowJumpToBottom(false);
            setNewMessagesPill(0);
            if (channelId) {
              delete scrollAnchorByChannel.current[channelId];
            }
          },
          getScrollState: () => {
            const el = scrollRef.current;
            if (!el) return null;
            return { scrollTop: el.scrollTop, scrollHeight: el.scrollHeight };
          },
        }),
        [channelId, scrollAnchorByChannel],
      );

      // ─── renderedRows ───────────────────────────────────────────────
      // Each row is a lightweight <MessageRow> stub that self-subscribes
      // via entrySource (push-based view or diff-based store for reconnect).
      // Only rows whose data actually changed re-render.
      const renderedRows = useMemo(
        () =>
          renderMessageRows({
            windowedItems,
            predecessorItem,
            channelId,
            highlightedMessageId,
            pinnedMessageIds,
            activeCallMessageId,
            entrySource,
            renderUserMap,
            workspaceMembers,
            workspaceChannels,
            activeWorkspaceMemberIds,
            reactionsMap,
            signalsMap,
            attachmentsMap,
            linkPreviewsMap,
            threadSourceIds,
            onThreadClick,
            onInlineReply,
            onCreateThread,
            onScrollToMessage,
            onPin,
            onUserClick,
            onSendToShell,
            onChannelClick,
            onJoinCall,
            activeMessageId,
            setMessageOpen,
          }),
        [
          windowedItems,
          predecessorItem,
          highlightedMessageId,
          pinnedMessageIds,
          activeCallMessageId,
          entrySource,
          renderUserMap,
          workspaceMembers,
          workspaceChannels,
          activeWorkspaceMemberIds,
          reactionsMap,
          signalsMap,
          attachmentsMap,
          linkPreviewsMap,
          threadSourceIds,
          onThreadClick,
          onInlineReply,
          onCreateThread,
          onScrollToMessage,
          onPin,
          onUserClick,
          onSendToShell,
          onChannelClick,
          onJoinCall,
          activeMessageId,
          setMessageOpen,
          channelId,
        ],
      );

      // ─── Render ─────────────────────────────────────────────────────
      csLog(
        "MessageListView.body",
        csRenderStart,
        `ch=${channelId?.slice(-6) ?? "-"} msgs=${messageListLength}`,
      );

      const content = (
        <div ref={scrollAreaRef} className="flex-1 relative min-h-0">
          {isLoadingOlder && <LoadingOlderPill />}
          {showJumpToBottom && (
            <JumpToBottomButton
              onClick={jumpToBottom}
              newMessagesCount={newMessagesPill}
            />
          )}
          <MessageScrollContainer
            channelId={channelId}
            rows={renderedRows}
            scrollRef={scrollRef}
            topSentinelRef={topSentinelRef}
            bottomSentinelRef={bottomSentinelRef}
            captureSnapshot={scrollAnchor.capture}
            restoreSnapshot={scrollAnchor.restore}
            lastSnapshotRef={scrollAnchor.lastSnapshotRef}
            isNearBottomRef={isNearBottomRef}
            currentAnchorIdRef={currentAnchorIdRef}
            scrollAnchorByChannel={scrollAnchorByChannel}
            messageIndexById={messageIndexById}
            messageWindow={messageWindow}
            windowBoundsKey={windowBoundsKey}
            scrollTargetPending={scrollTargetPending}
            isLoadingOlder={isLoadingOlder}
            hasMoreMessages={hasMoreMessages}
            channelMessagesStatus={channelScrollMessagesStatus}
            showMessageLoadingSkeleton={showMessageLoadingSkeleton}
            isUsingReconnectSnapshot={isUsingReconnectSnapshot}
            messageListLength={messageListLength}
            onNearBottomChange={onNearBottomChange}
          />
          {onSendToShell && (
            <SelectionToolbar
              containerRef={scrollRef}
              onSendToShell={onSendToShell}
            />
          )}
        </div>
      );

      // Singleton MessageActions path: wrap with hover provider + render
      // ONE actions pill (portal'd to body, positioned over hovered row).
      // Each `<Message />` checks the same flag and skips its own
      // `<MessageActions />` mount, slashing the per-shift mount storm
      // (~200 rows × 7 Radix Tooltip subtrees = the 450ms phase-2
      // hitch).
      if (SINGLETON_MESSAGE_ACTIONS) {
        return (
          <HoveredMessageProvider>
            {content}
            <HoveredMessageActions />
          </HoveredMessageProvider>
        );
      }
      return content;
    },
  ),
);
